import { RefObject } from "react";

import qs from "query-string";

import {
    ENDPOINTS,
    FILTER_NAMES,
    isInternalTypeCompany,
    NOT_ASSIGNED,
    ORDERS,
    parseResource,
    Query,
    QueryParams
} from "shared";

import { Description } from "../types";

export const getDescription = (
    option: any,
    description?: Description | Description[]
) => {
    if (!description) return {};

    const descriptionArray = Array.isArray(description)
        ? description
        : [description];

    const parsedDescription = descriptionArray.map(({ resource, name }) => ({
        value: parseResource(resource, option),
        ...(name && { name })
    }));

    const filteredDescription = parsedDescription.filter(({ value }) => value);

    const descriptionValue = filteredDescription.map(({ name, value }) =>
        name ? `${name}: ${value}` : value
    );

    return { description: descriptionValue.join("; ") };
};

export const getFilteredData = (
    dataFromQuery: Query[],
    exceptions?: number | Array<number>,
    hasOnlyInternalCompanies?: boolean,
    hasNotAssigned?: boolean
) => {
    const filterInternalCompanies = (data: Query[]) => {
        const dataCopy = [...data];

        if (hasOnlyInternalCompanies) {
            return dataCopy.filter(data =>
                isInternalTypeCompany(data.companyInfo?.companyType)
            );
        }

        return dataCopy;
    };

    const filterExceptions = (data: Query[]) => {
        if (exceptions) {
            const isArray = Array.isArray(exceptions);
            const exceptionsArray = isArray ? exceptions : [exceptions];

            return data.filter(({ id }) => !exceptionsArray.includes(id));
        }

        return data;
    };

    const dataAfterInternalCompaniesFilter =
        filterInternalCompanies(dataFromQuery);

    const dataAfterExceptionsFilter = filterExceptions(
        dataAfterInternalCompaniesFilter
    );

    if (hasNotAssigned) {
        dataAfterExceptionsFilter.unshift({
            id: -1,
            name: NOT_ASSIGNED
        });
    }

    return dataAfterExceptionsFilter;
};

export const getAdditionalQueryInfo = (option: any, resourceName: string) => {
    if (resourceName === ENDPOINTS.Groups) {
        return {
            groupCompanyInfo: {
                companyId: option.company_id,
                parentName: option.company.name
            }
        };
    }

    if (resourceName === ENDPOINTS.Companies) {
        return {
            companyInfo: {
                lzCode: option.code,
                vipStatus: option.vip_status || null,
                companyType: option.type || null,
                companyLevel: option.level || null,
                ...(option.company
                    ? {
                          parentCompany: {
                              id: option.company.id,
                              name: option.company.name
                          }
                      }
                    : {})
            }
        };
    }

    if (resourceName === ENDPOINTS.Roles) {
        return {
            permissions: option.permissions || [],
            roleType: option.type
        };
    }

    return {};
};

export const getCompanySelectorParams = (
    allQueryParams: QueryParams,
    isRootRequired?: boolean,
    isCompanyIdRequired?: boolean
) => ({
    ...(isRootRequired
        ? {
              [FILTER_NAMES.RootCompany]:
                  allQueryParams[FILTER_NAMES.RootCompany]
          }
        : {}),
    ...(isCompanyIdRequired
        ? { [FILTER_NAMES.CompanyId]: allQueryParams[FILTER_NAMES.CompanyId] }
        : {})
});

export const getResourceParams = (params: string) =>
    params
        ? qs.parse(params, {
              arrayFormat: "comma"
          })
        : {};

export const getSortParams = (resourceName: string, isAdminUser: boolean) => {
    const isFilesResource = resourceName === ENDPOINTS.Files;
    const isCompaniesResource = resourceName === ENDPOINTS.Companies;
    const isRolesResource = resourceName === ENDPOINTS.Roles;

    if (isFilesResource) {
        return { sort: "id", order: ORDERS.Descending };
    }

    return (isAdminUser && isCompaniesResource) || isRolesResource
        ? {}
        : { sort: "name", order: ORDERS.Ascending };
};

export const getQueryParams = (query: string) => (query ? { query } : {});

export const handleInitialOptionsLoad = (
    lastResourceRef: RefObject<string | null>,
    filteredData: Query[],
    resource: string,
    onInitialOptionsLoad?: (options: Query[]) => void
) => {
    if (onInitialOptionsLoad && lastResourceRef.current !== resource) {
        onInitialOptionsLoad(filteredData);
        lastResourceRef.current = resource;
    }
};
